<template>
    <!-- Noeud avec ses enfants -->
    <li :id="HeritageTreeUid + '-HeritageNode' + node.Id"
        :class="['Node', { Selected: node.CheckLeader }]">
        <!-- Seulement la ligne de noeud -->
        <div v-show="!node.Hidden" 
            @dragenter="dropZoneHoverStyle"
            @dragover.prevent="dropZoneHoverStyle"
            @drop.prevent="onDrop($event)"
            @dragleave="onDragLeave"
            v-on="node.Disabled ? {} : { click: () => selection(false), contextmenu: (e) => { displayMenu(e); }}"
            :class="['NodeInfoLine text-nowrap', node.Selected ? 'text-' + Variant + ' bg-' + Variant + '-bg-subtle' : '']">

            <!-- Icone sur la gauche du noeud pour déployer ou replier les noeuds enfants -->
            <span v-if="node.RightBound - node.LeftBound > 1 && !(node.HiddenCaret)" @click.stop.prevent="toggleOpener()" 
                style="display: inline-block;" :style="node.NestedChecked ? 'color: var(--' + Variant + ');' : ''">
                <b-icon v-if="node.Expanded" icon="caret-down-fill" :variant="node.Selected ? Variant : 'gray-dark'" class="mr-1 mb-1 align-middle" />
                <b-icon v-else icon="caret-right-fill" variant="gray-dark" class="mr-1 mb-1 align-middle" />
            </span>
            <span v-else class="mr-1"><b-icon /></span>

            <!-- Case à cocher liée aux filtres -->
            <b-form-checkbox v-if="node.WithCheckBox" inline plain
                :checked="node.Checked" :indeterminate="node.Indeterminate"
                :disabled="node.Disabled"
                class="m-0 p-0" :class="node.NestedChecked ? 'nestedChecked' : ''" />

            <!-- Partie du noeud qui va rester sous le curseur lors d'un drag&drop (uniquement disponible en mode affichage) -->
            <!-- Attention : l'event associé diffère selon le type de noeud et qu'il s'agisse d'un SingleNode ou non -->
            <!-- Application de la classe 'text-gray-light' si node.Reference est renseigné pour les noeuds ghost OU si node.Disabled -->
            <span :draggable="IsDraggable"
                :class="['p-0 text-nowrap d-inline-block mw-100', { 'text-danger': (node.Depreciated && !node.Reference), 'text-gray-dark': ((node.Reference || (node.Disabled && node.WithCheckBox)) && !node.Depreciated), 'text-danger-middle-dark': (node.Reference && node.Depreciated), 'font-italic text-gray-dark' : (node.IsAncestor) }
                    , node.Selected ? 'text-' + Variant + ' bg-' + Variant + '-bg-subtle' : '']"
                style="text-overflow: ellipsis !important;"
                @dragstart="startDrag($event, node)"
                @dragend="onDragEnd">
                <div class="p-0 m-0"
                    :style="node.NestedChecked ? 'color: var(--' + Variant + ');' : ''">
                    <b-img :draggable="IsDraggable" :src="AbsoluteLink(node.Icon)" class="ml-0 mr-1" />
                    {{ node.Name }}
                </div>
            </span>
        </div>
        <!-- Liste des noeuds enfant du noeud courant -->
        <transition name="node-opening">
            <ul v-if="node.Expanded || node.Forced">
                <!-- v-on="$listeners" utilisé pour passer les events à Heritage-Node à travers la hiérarchie d'HeritageNode -->
                <heritage-node v-for="nodeChild in childNodes(node)" :key="nodeChild.Id" :node="nodeChild" :FilterTreeStore="FilterTreeStore" v-on="$listeners" />
            </ul>
        </transition>
    </li>
</template>

<script lang="ts">
    import Component, { mixins } from 'vue-class-component';
    import { InjectReactive, Prop } from 'vue-property-decorator';
    import { Getter } from 'vuex-class';
    import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
    import TreeNode from '@/types/TreeNode';
    import { LinkUtils, ObjectUtils } from "@/components/commons/Utils";

    /** Composant de noeud de l'arborescence */
    @Component({
        name: 'heritage-node',
        components: {
            FontAwesomeIcon,
            HeritageNode
        }
    })
    export default class HeritageNode extends mixins(LinkUtils, ObjectUtils) {
		/** 
		 * Variant de couleur à appliquer sur le composant
		 * @default secondary
		 */
        @InjectReactive()
		public Variant!: string;

        /** Indique l'uid de l'HeritageTree courant à tous les HeritageNode pour distinguer les instances d'arborescence */
        @InjectReactive()
		public HeritageTreeUid!: string;

        /** Noeud courant */
        @Prop()
        public node!: TreeNode;

		/** Nom du store Vuex persistant l'arborescence */
		@Prop()
        public FilterTreeStore!: string;

        private get childNodes() {
			return this.$store.getters[this.FilterTreeStore + '/ChildNodes'];
		}

        @Getter('ObjectMoveStore/CheckConstraint')
        private checkConstraint!: (targetObjectType: string) => boolean;

        /** Indique si le noeud peut-être drag&dropé */
        @InjectReactive()
        public IsDraggable!: boolean;

        private toggleOpener() {
            this.node.Expanded = !this.node.Expanded;
        }

        private selection(isContextMenu: boolean) {
            if(!(this.$store.getters[this.FilterTreeStore + '/StopPropagation'](isContextMenu, this.node))) {
                //Pas sur qu'il faut mettre ça la car pas de mise à jour de l'objet si pas en mode affichage
                this.$store.dispatch(this.FilterTreeStore + "/SelectNode", { node: this.node, scrollIntoView: false });
            }
        }

        /** Indique si le drag'n'drop est autorisé ou non */
        private authorization: boolean = false;

        private startDrag(evt, item: TreeNode) {
            this.$store.dispatch("ObjectMoveStore/StartMoveObject", { sourceObjectId: item.Id, sourceObjectType: item.ObjectType, sourceObjectName: item.Name });
        }

        private async onDrop(evt) {
            if (this.authorization) {
                /** Réalise le drop */
                await this.$store.dispatch("ObjectMoveStore/EndMoveObject", this.node.Id);
            }

            /** Emet un évènement au Workbench pour afficher l'info-bulle contenant une icone + ou - pour indiquer si le drop sur l'objet est autorisé ou non */
            this.onDragLeave(evt);
        }

        /**
         * Styles appliqués lors que l'on drag un noeud
         * @param e Evènement
         */
        private dropZoneHoverStyle(e: Event) {
            let sourceObjectId: string = this.$store.getters['ObjectMoveStore/GetSourceObjectId'];

            /** Si le noeud source et le noeud cible ne sont pas les mêmes */
            this.authorization = (sourceObjectId !== this.node.Id);

            if (this.authorization) {
                let parentNodeId = this.$store.getters[`${this.FilterTreeStore}/ParentNodeId`]({ childNodeId: this.node.Id });
                console.debug("ParentNodeId : ", parentNodeId);

                this.authorization = (
                    /** Si le noeud cible n'est pas un enfant direct du noeud source et que les types sources et cibles permettent le drag&drop */
                    sourceObjectId !== parentNodeId && this.checkConstraint(this.node.ObjectType)
                );
            }

            this.$emit("ShowTooltip", e, this.authorization);

            let target = e.currentTarget as HTMLElement;
            if (target)
                target.classList.add('dropZone');
        }

        /**
         * Lorsque le drag est quitté. Suppression des styles appliqués.
         * @param e Evènement
         */
        private onDragLeave(e) {
            this.authorization = false;
            this.$emit("HideTooltip");

            const target = e.currentTarget as HTMLElement;
            if (target)
                target.classList.remove('dropZone');
        }

        /**
         * Lorsque le drag se termine
         * @param e Evènement
         */
        private async onDragEnd(e) {
            if (this.authorization)
                await this.$store.dispatch("ObjectMoveStore/EndMoveObject");

            this.onDragLeave(e);
        }

        /** 
         * Informe le Workbench d'afficher le menu contextuel
         * @param e Events
         */
        private displayMenu(e) {
            this.$emit('DisplayContextMenuEvent', e, e.pageX, e.pageY, this.node);
        }
    }
</script>

<style scoped>
    .Node {
        position: relative;
        display: block;
        box-sizing: border-box;
        padding-left: 10px;
        right: 0px;
        transition-duration: 0.15s;
        vertical-align: text-top;
        /* font-size: 0.64rem; */
        /* margin: 2px 0px 3px 2px; */
        border-right: 0px;
        border-radius: 3px;
        -webkit-touch-callout: none; /* iOS Safari */
        -webkit-user-select: none; /* Safari */
        -khtml-user-select: none; /* Konqueror HTML */
        -moz-user-select: none; /* Old versions of Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
        user-select: none; /* Non-prefixed version, currently
        supported by Chrome, Edge, Opera and Firefox */
    }

        .Node img {
            display: inline-block;
            width: 20px;
            height: 20px;
            margin: 0px 10px 0px 0px;
        }


    .Node:hover {
        cursor: pointer;
    }

    .Node.Selected {
        border: 1px solid var(--Variant);
        margin-left: 0px;
    } 

    ul {
        box-sizing: border-box;
        /*padding-inline-start: 10px !important;*/
        writing-mode: horizontal-tb;
        padding-left: 10px !important;
        margin-bottom: 0px !important;
        width: 100%;
        height: 100%;
    }

    li {
        list-style: none;
        list-style-position: inside;
        border-top: 0px;
        border-bottom: 0px;
    }

    :deep(input[type=checkbox]:checked) {
        /** Change la couleur de la checkbox */
        accent-color: var(--Variant) !important;
    }

    :deep(.nestedChecked > input[type=checkbox]) {
        box-shadow: 0px 0px 0px 1px var(--Variant) !important;
    }

    .NodeInfoLine > * {
        vertical-align: middle;
        padding-top: 0px;
    }

    .NodeInfoLine:hover {
        color: var(--gray-dark);
        background-color: var(--gray);
    }

    /** Zone où l'on peut relacher le curseur lors du drag'n'drop */
    .dropZone {
        background-color: var(--VariantBgSubtle) !important;
        color: var(--Variant) !important;
    }

</style>